import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ImageUpload, defaultAvatar } from "@/components/ImageUpload";
import { Crown, Loader2, TrendingUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function BidModal({ open, onOpenChange, slug, keywordName, topBid, existingBid, onSuccess, symbol = "$", fee = 5 }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [businessName, setBusinessName] = useState("");
  const [website, setWebsite] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [location, setLocation] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [mapsUrl, setMapsUrl] = useState("");
  const [phone, setPhone] = useState("");
  const [newBid, setNewBid] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [balance, setBalance] = useState(null);

  const loadWallet = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/wallet", { params: { region: "global" } });
      setBalance(data.wallet.balance);
    } catch {}
  }, [user]);

  const currentLocked = Number(existingBid?.locked_bid_amount || 0);
  const rank1Locked = Number(topBid || 0);
  const minBid = currentLocked + 1;
  const minToTakeRank1 = rank1Locked + 1;
  const delta = Math.max(0, Number(newBid) - currentLocked);
  const upgradeInsufficient = balance !== null && delta > Number(balance);

  useEffect(() => {
    if (open) {
      setBusinessName(existingBid?.business_name || user?.name || "");
      setWebsite(existingBid?.website || "");
      setLogoUrl(existingBid?.logo_url || user?.picture || "");
      setWhatsapp(existingBid?.whatsapp_number || "");
      setLocation(existingBid?.location_text || "");
      setShortDesc(existingBid?.short_description || "");
      setMapsUrl(existingBid?.google_maps_url || "");
      setPhone(existingBid?.phone_number || "");
      setNewBid(Number(existingBid?.locked_bid_amount || 0) + 1);
      loadWallet();
    }
  }, [open, existingBid, user, topBid, loadWallet]);

  const listingPayload = () => ({
    keyword_slug: slug,
    business_name: businessName.trim(),
    website: website.trim(),
    logo_url: logoUrl.trim() || defaultAvatar(businessName.trim()),
    whatsapp_number: whatsapp.trim() || null,
    location_text: location.trim() || null,
    short_description: shortDesc.trim() || null,
    google_maps_url: mapsUrl.trim() || null,
    phone_number: phone.trim() || null,
  });

  const submit = async () => {
    if (!businessName.trim()) { toast.error("Business name is required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bids", listingPayload());
      toast.success(existingBid ? "Listing updated" : `Bid placed! Spot Price: ${symbol}${fee}`);
      onOpenChange(false);
      onSuccess && onSuccess(data.leaderboard);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const submitUpgrade = async () => {
    if (Number(newBid) < minBid) { toast.error(`Enter at least ${symbol}${minBid}`); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/bids/${existingBid.bid_id}/upgrade`, { new_locked_bid: Number(newBid) });
      toast.success(`Locked bid raised to ${symbol}${data.locked_bid_amount}`);
      onOpenChange(false);
      onSuccess && onSuccess(data.leaderboard);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 text-white max-w-md" data-testid="bid-modal">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-2xl tracking-tight flex items-center gap-2">
            <Crown className="w-6 h-6 text-emerald-400" />
            {existingBid ? "Update your bid" : "Claim the top spot"}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            on <span className="text-white font-medium">"{keywordName}"</span>
          </DialogDescription>
        </DialogHeader>

        {!user ? (
          <div className="py-6 text-center space-y-4">
            <p className="text-white/60">You need an account to place a bid.</p>
            <Button data-testid="bid-signin-btn" onClick={() => navigate("/login")} className="rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 font-semibold active:scale-95 transition-all">
              Sign in to bid
            </Button>
          </div>
        ) : (
          <div className="space-y-5 py-2">
            <div>
              <Label className="text-white/70 text-sm">Business / creator name</Label>
              <Input data-testid="bid-name-input" value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Acme Coffee Co." className="mt-1.5 bg-white/5 border-white/15 focus-visible:ring-blue-500/50" />
            </div>
            <div>
              <Label className="text-white/70 text-sm">Website URL</Label>
              <Input data-testid="bid-website-input" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://acme.com" className="mt-1.5 bg-white/5 border-white/15 focus-visible:ring-blue-500/50" />
            </div>
            <div>
              <Label className="text-white/70 text-sm">WhatsApp number <span className="text-white/30">(optional)</span></Label>
              <Input data-testid="bid-whatsapp-input" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="60123456789" className="mt-1.5 bg-white/5 border-white/15 focus-visible:ring-blue-500/50" />
            </div>
            <div>
              <Label className="text-white/70 text-sm">Location <span className="text-white/30">(optional)</span></Label>
              <Input data-testid="bid-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Puchong Utama" className="mt-1.5 bg-white/5 border-white/15 focus-visible:ring-blue-500/50" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-white/70 text-sm">Short description <span className="text-white/30">(optional)</span></Label>
                <span className="text-xs text-white/30">{shortDesc.length}/100</span>
              </div>
              <Input data-testid="bid-shortdesc-input" value={shortDesc} maxLength={100} onChange={(e) => setShortDesc(e.target.value)} placeholder="Best specialty coffee in town" className="mt-1.5 bg-white/5 border-white/15 focus-visible:ring-blue-500/50" />
            </div>
            <div>
              <Label className="text-white/70 text-sm">Google Maps Link <span className="text-white/30">(optional)</span></Label>
              <Input data-testid="bid-maps-input" value={mapsUrl} onChange={(e) => setMapsUrl(e.target.value)} placeholder="https://maps.app.goo.gl/…" className="mt-1.5 bg-white/5 border-white/15 focus-visible:ring-blue-500/50" />
              <p className="text-xs text-white/40 mt-1.5">Paste your Google Maps share link here. Open Google Maps → find your location → click Share → Copy link.</p>
            </div>
            <div>
              <Label className="text-white/70 text-sm">Phone Number <span className="text-white/30">(optional)</span></Label>
              <Input data-testid="bid-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" className="mt-1.5 bg-white/5 border-white/15 focus-visible:ring-blue-500/50" />
            </div>
            <div>
              <ImageUpload value={logoUrl} onChange={setLogoUrl} seedName={businessName} label="Logo / avatar" testId="bid-logo-upload" />
            </div>

            {existingBid ? (
              <>
                <div className="glass rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-white/70 text-sm">Enter your new Total Bid</Label>
                    <span className="text-xs text-white/40">Current: {symbol}{currentLocked}</span>
                  </div>
                  <Input data-testid="upgrade-bid-input" type="number" min={minBid} value={newBid}
                    onChange={(e) => setNewBid(e.target.value)}
                    className="bg-white/5 border-white/15 focus-visible:ring-emerald-500/50" />
                  <div className="flex gap-2 mt-2" data-testid="upgrade-presets">
                    <Button data-testid="upgrade-preset-1" type="button" variant="outline" size="sm"
                      onClick={() => setNewBid(currentLocked + 1)}
                      className="flex-1 rounded-full border-white/15 hover:bg-white/10 text-xs">+{symbol}1</Button>
                    <Button data-testid="upgrade-preset-5" type="button" variant="outline" size="sm"
                      onClick={() => setNewBid(currentLocked + 5)}
                      className="flex-1 rounded-full border-white/15 hover:bg-white/10 text-xs">+{symbol}5</Button>
                    <Button data-testid="upgrade-preset-rank1" type="button" variant="outline" size="sm"
                      onClick={() => setNewBid(minToTakeRank1)}
                      className="flex-1 rounded-full border-amber-500/30 text-amber-300 hover:bg-amber-500/10 text-xs">Take #1</Button>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-white/50">
                    <TrendingUp className="w-3.5 h-3.5" />
                    Minimum to take #1: {symbol}{minToTakeRank1}
                  </div>
                  <div data-testid="upgrade-cost" className="mt-2 font-display font-bold text-emerald-400">
                    Cost to upgrade: {symbol}{delta.toFixed(2)}
                  </div>
                </div>
                {upgradeInsufficient && (
                  <div data-testid="upgrade-insufficient" className="flex items-start gap-3 rounded-xl border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm text-yellow-100">
                    <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                    <span>Insufficient balance — <Link data-testid="upgrade-topup-link" to="/wallet" onClick={() => onOpenChange(false)} className="underline font-semibold hover:text-white">Top up your wallet</Link> to proceed.</span>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button data-testid="upgrade-submit-button" onClick={submitUpgrade} disabled={submitting || Number(newBid) < minBid || upgradeInsufficient}
                    className="flex-1 rounded-full h-12 bg-gradient-to-r from-amber-500 to-yellow-500 hover:opacity-90 active:scale-95 transition-all font-semibold text-black disabled:opacity-40">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Outbid for ${symbol}${delta.toFixed(2)}`}
                  </Button>
                  <Button data-testid="bid-savedetails-button" variant="outline" onClick={submit} disabled={submitting}
                    className="rounded-full h-12 border-white/15 hover:bg-white/10">Save details</Button>
                </div>
              </>
            ) : (
              <>
                <div className="glass rounded-xl p-4 text-sm text-white/70" data-testid="placement-fee-note">
                  One-time <span className="text-white font-semibold">Spot Price</span> of {symbol}{fee} is deducted from your wallet to claim your spot. No recurring charges — your bid is permanent.
                </div>
                <Button data-testid="bid-submit-button" onClick={submit} disabled={submitting}
                  className="w-full rounded-full h-12 bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 active:scale-95 transition-all font-semibold text-black">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Place bid — ${symbol}${fee}`}
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
